import Chart from './Chart';
import Tooltip from './Tooltip';
import * as d3 from 'd3';

export default class Pie extends Chart {
  constructor(svg, height, width, margin = 10, trnsTime = 0, tooltipDiv) {
    super(svg, height, width, margin, trnsTime);
    this.tooltip = new Tooltip(tooltipDiv);
    this.yearRange = [2000, 2018];
    this._initializeHelperFunctions();
    this._initializeChart();
  }

  _initializeHelperFunctions(innerRadius = 0) {
    this.calcArcs = d3.pie()
      .sort((a, b) => d3.ascending(a.total, b.total))
      .value(data => data.total);

    this.calcArcPath = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(Math.min(this.width, this.height) / 2 - this.margin);

    // const radius = Math.min(this.width, this.height) / 2 * 0.8;
    // this.calcLabelPosition = d3.arc()
    //   .innerRadius(radius)
    //   .outerRadius(radius);
  }

  _initializeChart() {
    this.svg
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio' , 'xMinYMin meet')
      .attr("text-anchor", "middle");
    
    this.slices = this.svg.append('g')
      .attr('transform', `translate(${this.width/2},${this.height/2})`);

    this.handleMouseOver = (d) => {
      this.tooltip.changeContent(`${d.data.key}: 
        ${(100 * (d.endAngle - d.startAngle) / (Math.PI * 2)).toFixed(1)}%`);
      this.tooltip.show();

      const [dx, dy] = this.calcSliceTranslate(d.startAngle, d.endAngle);
      
      d3.select(d3.event.target)
        .transition()
        .duration(this.transitionTime / 10)
          .attr('transform', `translate(${dx},${dy})`)
          .style('cursor', 'pointer')
          .style("opacity", 1)
          .ease(d3.easeSinInOut);
    };

    this.handleMouseLeave = () => {
      this.tooltip.hide();
      d3.select(d3.event.target)
        .transition()
        .duration(this.transitionTime / 1.5)
          .attr('transform', `translate(${0},${0})`)
          .style("opacity", 0.6)
          .ease(d3.easeBounce);
    };

    this.handleMouseMove = () => {
      this.tooltip.move(d3.event.pageX + 10, d3.event.pageY - 25);
    }

    this.handleClick = () => {
      
    }
  }

  setData(data) {
    this.data = this.calcArcs(data);
    this.color.domain(this.data.map(d => d.data.key).sort(d3.ascending));
  }
  
  setYearRange([min, max]) {
    this.yearRange = [min, max];
  }

  _filterData() {
    const [minYear, maxYear] = this.yearRange;

    let data = this.data.map(d => d.data);

    data.forEach(val => {
      val.total = d3.sum(val.value, d => 
        (d.year >= minYear && d.year <= maxYear) ?  d.value : 0)
    });

    return data;
  }

  setInnerRadius(innerRadius) {
    this._initializeHelperFunctions(innerRadius);
  }

  drawChart() {
    this._drawSlices();
  }

  _drawSlices() {
    this.slices.selectAll('*').remove();

    const data = this.calcArcs(this._filterData());

    this.slices
      .selectAll('path')
      .data(data)
      .enter()
      .append('path')
        .attr('fill', d => this.color(d.data.key))
        .style('pointer-events', 'none')
      .transition('draw')
      .duration(this.transitionTime)
        .attrTween('d', d => {
          const i = d3.interpolate(d.startAngle, d.endAngle);
          return (t) => {
            d.endAngle = i(t);
            return this.calcArcPath(d);
          };
        })
        .ease(d3.easeSinInOut)
      .on('end', function () {
        d3.select(this).style('pointer-events', 'all');
      });
      
    this.slices
      .selectAll('path')
        .attr('stroke', 'white')
        .attr('stroke-width', '1')
        .attr('opacity', 0.6);

    this.slices
      .selectAll('path')
        .on('mouseover', this.handleMouseOver)
        .on('mousemove', this.handleMouseMove)
        .on('mouseleave', this.handleMouseLeave)
        .on('click', this.handleClick);
  }

  updateChart() {
    const data = this.calcArcs(this._filterData());
    const oldData = this.slices.selectAll('path').data();

    this.slices
      .selectAll('path')
      .data(data)
        .style('pointer-events', 'none')
      .transition('update')
      .duration(this.transitionTime)
        .attrTween('d', (d, i) => {
          const j = d3.interpolate(oldData[i].endAngle, d.endAngle);
          const k = d3.interpolate(oldData[i].startAngle, d.startAngle);
          return (t) => {
            d.startAngle = k(t);
            d.endAngle = j(t);
            return this.calcArcPath(d);
          };
        })
        .ease(d3.easeSinInOut)
      .on('end', function () {
        d3.select(this).style('pointer-events', 'all');
      });
  }

  setOnSliceMouseMove(onMouseMove) {
    this.handleMouseMove = onMouseMove;
  }

  setOnSliceMouseOver(onMouseOver) {
    this.handleMouseOver = onMouseOver;
  }

  setOnSliceMouseLeave(onMouseLeave) {
    this.handleMouseLeave = onMouseLeave;
  }

  setOnClick(onClick) {
    this.handleClick = onClick;
  }

  calcSliceTranslate(startAngle, endAngle) {
    const r = 10,
          angle = (endAngle - startAngle) / 2 + startAngle,
          dx = Math.cos(Math.PI / 2 - angle) * r,
          dy = -Math.sin(Math.PI / 2 - angle) * r;

    return [dx, dy];
  }
}
